import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import AlertMessageDialog from "../../../../components/Dialogs/alertMessageDialog";

/**
 * AddNoteDialog
 *
 * Props:
 * - open: boolean
 * - loanId?: string
 * - onClose: () => void
 * - onSave?: (payload: { html: string; text: string; files: File[] }) => void
 */
export default function AddNoteDialog({ open, loanId, loanInfo, onClose, onSave }) {
  const theme = useTheme();
  const editorRef = React.useRef(null);
  const [files, setFiles] = React.useState([]);
  
  // State for alert dialog
  const [alertMessage, setAlertMessage] = React.useState({
    open: false,
    title: "",
    message: "",
    severity: "info",
  });

  const exec = (cmd) => {
    if (!editorRef.current) return;
    document.execCommand(cmd, false, null);
    editorRef.current.focus();
  };

  const onDropFiles = (e) => {
    const f = e.target.files ? Array.from(e.target.files) : [];
    if (f.length) setFiles((prev) => [...prev, ...f]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const showAlertMessage = ({ title, message, severity = "info" }) => {
    setAlertMessage({
      open: true,
      title,
      message,
      severity,
    });
  };

  const handleCloseAlertMessage = () => {
    setAlertMessage((prev) => ({ ...prev, open: false }));
  };


    // Handle saving note
    const handleSaveNote = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/${loanId}/notas`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
            },
            body: JSON.stringify({
                id_prestamo: loanId,
                nota: editorRef.current.textContent,
                created_by: localStorage.getItem('id'),
            })
            }); 

            if (!response.ok) {
            throw new Error('Error al guardar la nota');
            }

            // Show success message
            showAlertMessage({
                title: "Éxito",
                message: "Nota guardada exitosamente",
                severity: "success",
            });
            
            // Close the dialog after a short delay to show the success message
            setTimeout(() => {
                onClose();
                // Reset the editor
                if (editorRef.current) {
                    editorRef.current.innerHTML = "";
                }
            }, 1500);
            
        } catch (error) {
            console.error('Error saving note:', error);
            
            // Show error message
            showAlertMessage({
                title: "Error",
                message: "Error al guardar la nota. Intente nuevamente.",
                severity: "error",
            });
        }
    };
    

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Agregar Nueva Nota
        {loanInfo && (
          <Typography variant="body2" color="text.secondary">Para el Préstamo ID: {loanInfo.loan_id}</Typography>
        )}
      </DialogTitle>
      <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Stack spacing={2}>
          <div>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Nota</Typography>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              {/* Toolbar */}
              <Stack direction="row" spacing={0.5} sx={{ p: 0.5, borderBottom: 1, borderColor: "divider" }}>
                <IconButton size="small" onClick={() => exec("bold")}><FormatBoldIcon fontSize="small"/></IconButton>
                <IconButton size="small" onClick={() => exec("italic")}><FormatItalicIcon fontSize="small"/></IconButton>
                <IconButton size="small" onClick={() => exec("underline")}><FormatUnderlinedIcon fontSize="small"/></IconButton>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
                <IconButton size="small" onClick={() => exec("insertUnorderedList")}><FormatListBulletedIcon fontSize="small"/></IconButton>
                <IconButton size="small" onClick={() => exec("insertOrderedList")}><FormatListNumberedIcon fontSize="small"/></IconButton>
              </Stack>
              {/* Editable content */}
              <Box
                ref={editorRef}
                contentEditable
                role="textbox"
                aria-multiline
                sx={{ p: 1.25, minHeight: 140, outline: "none", typography: 'body2' }}
                data-placeholder="Ingrese los detalles de la nota aquí..."
                onKeyDown={(e)=>{
                  // prevent MUI dialog from closing on Enter if needed
                }}
              />
            </Box>
          </div>

       {/*   <div>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Attachments</Typography>
            <Box
              component="label"
              htmlFor="note-file-input"
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                bgcolor: 'grey.50', height: 128, cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              <Stack alignItems="center" spacing={0.5}>
                <CloudUploadIcon sx={{ fontSize: 36, color: 'text.secondary' }}/>
                <Typography variant="body2" color="text.secondary"><b>Click to upload</b> or drag and drop</Typography>
                <Typography variant="caption" color="text.secondary">SVG, PNG, JPG or PDF</Typography>
              </Stack>
              <input id="note-file-input" type="file" multiple hidden onChange={onDropFiles} />
            </Box>
            {files.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                {files.map((f, idx) => (
                  <Chip
                    key={idx}
                    icon={<DescriptionIcon />}
                    label={`${f.name} (${Math.round(f.size/1024)}KB)`}
                    onDelete={() => removeFile(idx)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
          </div>*/}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveNote} sx={{fontWeight:"bold"}} color="primarybutton">Guardar Nota</Button>
        </Stack>
      </DialogActions>
      
      <AlertMessageDialog
        open={alertMessage.open}
        setOpen={setAlertMessage}
        title={alertMessage.title}
        message={alertMessage.message}
        severity={alertMessage.severity}
        handleClose={handleCloseAlertMessage}
      />
    </Dialog>
  );
}

